import { useEffect, useMemo, useState } from "react";
import { BellRinging, DeviceMobile } from "@phosphor-icons/react";
import {
  readPushStatus,
  subscribeToPush,
  unsubscribeFromPush,
  type PushState
} from "../pwa/push";
import { useInstallPrompt } from "../pwa/useInstallPrompt";
import { toPortalFailure } from "../lib/errors";
import { readPlatform } from "../pwa/platform";
import { Panel } from "../ui/Panel";
import "./clientSettings/settings.css";

/**
 * Den här enheten: push notifications and installation, which are the two things about the portal
 * that are true of a device rather than of an account.
 *
 * They share a panel because they share a subject and because they are the same decision to a
 * person: on iOS, push works only from the installed home-screen app and not from a browser tab
 * (plan.md section 14.10, "the only path where push works on iOS at all"). Separating them would put
 * the prerequisite on a different screen from the thing it is a prerequisite for.
 *
 * **Until 2026-09-08 the panel told an iPhone two false things at once.** The only sentence in the
 * portal explaining how to install on iOS sat inside `{canInstall ? ...}`, and `canInstall` comes
 * from `beforeinstallprompt`, which Safari never fires. So the iPhone instruction rendered on Chrome
 * and Android, where it is wrong, and never on the platform that has no other way in. At the same
 * time an iOS tab has no `PushManager`, so the panel said "Den här webbläsaren har inte notiser" to
 * a browser that has them. Both halves now come from `readPlatform`, and the notification half
 * distinguishes a browser that cannot from a browser that cannot yet.
 *
 * **Nothing here asks for permission on its own.** plan.md section 5.7, "Push permission is requested
 * only after a user action": the prompt happens after a user action. The button is the action, and
 * `subscribeToPush` opens with the prompt.
 *
 * **A box of lines since the second build round of 2026-09-24** (`prototype/kund/pages.js`, the
 * `notiser` section): the push state as a line with its one button, then the install line. The lead
 * that said the panel is about the device and not the account is the heading's one line now. The
 * panel draws its own box, which is what `AdminSettingsRoute.tsx` shows on Petrovic's side too.
 */

const PUSH_TEXT: Record<PushState, { title: string; body: string }> = {
  on: {
    title: "Notiser är på för den här enheten",
    body: "Du får en avisering när något händer. Den säger att något hänt och aldrig vad, så det som står på en låst skärm avslöjar ingenting."
  },
  off: {
    title: "Notiser är av för den här enheten",
    body: "Slå på dem så säger portalen till när vi svarar, när ett ärende flyttas eller när ett dokument kommer."
  },
  blocked: {
    title: "Webbläsaren har blockerat notiser",
    body: "Det går inte att fråga igen härifrån. Tillåt notiser för den här sidan i webbläsarens inställningar, så fungerar knappen sedan."
  },
  unsupported: {
    title: "Den här webbläsaren har inte notiser",
    body: "Allt som skulle ha kommit som avisering finns kvar under Notiser i portalen och i mejlen."
  },
  /**
   * The iPhone answer, and it is a step rather than a refusal.
   *
   * The version sentence is there because this is the one place the portal would otherwise promise
   * something it cannot check from a tab. Home-screen web push arrived in iOS 16.4, and nothing in a
   * browser tab can tell whether this iPhone has it. Saying the requirement out loud is honest;
   * saying "then it will work" would not be.
   */
  "needs-install": {
    title: "Notiser fungerar när portalen ligger på hemskärmen",
    body: "På iPhone och iPad kan bara den installerade portalen ta emot notiser, aldrig en flik. Lägg till portalen på hemskärmen, öppna den därifrån och slå på notiser där. Det kräver iOS 16.4 eller senare."
  },
  unconfigured: {
    title: "Notiser är inte påslagna i den här miljön",
    body: "Portalen är byggd utan nyckeln som krävs. Det är en driftsak och inte något du har gjort."
  }
};

/**
 * The "off" sentence on Petrovic's own screen. The client's sentence above lists what a client is
 * told about, and on /admin/clients that list is wrong: Petrovic's push is a client writing. Decided
 * by Mattej 2026-09-24, docs/polish.md P-18. Every other sentence in the panel is true for either
 * kind of account, so only this one has an admin version.
 */
export const ADMIN_PUSH_OFF_BODY = "Slå på dem så säger portalen till när en kund skriver.";

/**
 * Whether this is Brave, asked of the runtime and never of the user-agent string, which Brave keeps
 * identical to Chrome's on purpose. Brave adds `navigator.brave`, and nothing else does.
 */
function isBrave(): boolean {
  return typeof navigator !== "undefined" && "brave" in navigator;
}

/**
 * Brave's refusal, build round 23 (2026-10-08). Brave ships with its push service switched off, so
 * `pushManager.subscribe` fails in the browser on every press, and "Försök igen" would be a retry that
 * cannot succeed. The browser's own failure is a `DOMException`; a refusal from the portal's
 * gateway is not one, and keeps the ordinary sentence, because these steps would not fix it.
 */
function isBravePushRefusal(error: unknown): boolean {
  return isBrave() && typeof DOMException !== "undefined" && error instanceof DOMException;
}

interface DevicePanelProps {
  /** Who is reading. `admin` is the panel on /admin/clients; everything else is a client. */
  audience?: "client" | "admin";
}

export function DevicePanel({ audience = "client" }: DevicePanelProps = {}) {
  const [status, setStatus] = useState<PushState | null>(null);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [braveRefused, setBraveRefused] = useState(false);
  const { canInstall, promptInstall } = useInstallPrompt();

  /**
   * Read once. Neither answer changes while the panel is mounted: a device does not stop being an
   * iPhone, and adding the portal to the home screen opens a new window rather than changing this
   * one.
   */
  const platform = useMemo(() => readPlatform(), []);

  useEffect(() => {
    // Guarded rather than fire-and-forget. Reading the subscription asks the service worker, which
    // takes a moment, and a person who opens settings and leaves before it answers must not have
    // state set on an unmounted panel.
    let active = true;

    void readPushStatus().then((next) => {
      if (active) {
        setStatus(next.state);
      }
    });

    return () => {
      active = false;
    };
  }, []);

  async function toggle(turnOn: boolean) {
    setFailure(null);
    setBraveRefused(false);
    setPending(true);

    try {
      const next = turnOn ? await subscribeToPush() : await unsubscribeFromPush();
      setStatus(next.state);
    } catch (error) {
      if (turnOn && isBravePushRefusal(error)) {
        setBraveRefused(true);
        return;
      }
      // The refusal says what was refused and not what was sent, which is the rule
      // `app/lib/errors.ts` sets for every other failure in the portal.
      //
      // Build round 54, 2026-10-31: one refusal has a sentence of its own, because trying again
      // cannot cure it. `subscribeToPush` has already dropped another account's subscription and
      // tried once more with a new one when this reaches the panel.
      const named = toPortalFailure(error);
      setFailure(
        turnOn && named.code === "PORTAL_PUSH_SUBSCRIPTION_CONFLICT"
          ? named.message
          : "Det gick inte att ändra notisinställningen för den här enheten. Försök igen."
      );
    } finally {
      setPending(false);
    }
  }

  const text = status
    ? status === "off" && audience === "admin"
      ? { ...PUSH_TEXT.off, body: ADMIN_PUSH_OFF_BODY }
      : PUSH_TEXT[status]
    : null;
  const canToggle = status === "on" || status === "off";

  /**
   * The instruction belongs to an iOS device that is still a browser tab, and to nothing else.
   *
   * Not to Android, which has its own prompt and no share sheet step, and not to an iPhone already
   * running the installed application, which would be told to do again what it has done.
   */
  const showIosInstall = platform.isIos && !platform.isStandalone;

  return (
    <Panel
      title="Den här enheten"
      titleId="device-title"
      hint="Aviseringar och installation gäller enheten du sitter vid, inte kontot. En ny telefon börjar om från av."
      testId="device-panel"
    >
      <div className="k-set-lines">
        <div className="k-set-line">
          <span className="type-icon" aria-hidden="true">
            <BellRinging size={18} />
          </span>
          <div className="primary-cell k-set-fact" data-testid="device-push-state">
            <strong>{text ? text.title : "Läser inställningen"}</strong>
            <span>{text ? text.body : "Ett ögonblick."}</span>
            {/*
              Computers only, and only where push is possible at all. Measured 2026-09-21 in Chrome on
              Windows: with every Chrome window closed the notice arrived when Chrome was started
              again, and not before. That is the browser and not the portal, and a person who does not
              know it concludes the notices are broken. A phone wakes the browser for a push, so this
              sentence would be false there.
            */}
            {platform.isDesktop && canToggle ? (
              <span data-testid="desktop-push-needs-browser">
                På en dator kommer notiserna medan webbläsaren är igång, och de som skickas när den är
                stängd kommer när du startar den igen.
              </span>
            ) : null}
          </div>
          {canToggle ? (
            <button
              type="button"
              className={status === "on" ? "button" : "button is-primary"}
              disabled={pending}
              onClick={() => void toggle(status !== "on")}
            >
              {pending
                ? "Ett ögonblick…"
                : status === "on"
                  ? "Stäng av notiser här"
                  : "Slå på notiser här"}
            </button>
          ) : null}
        </div>

        {failure ? (
          <p className="form-error" role="alert">
            {failure}
          </p>
        ) : null}

        {/*
          In place of the retry sentence on Brave, the four steps that make the press work. The
          address is text to copy and not a link: browsers refuse to open a brave:// address from a
          page.
        */}
        {braveRefused ? (
          <div className="form-error" role="alert" data-testid="device-push-brave">
            <ol>
              <li>Brave stänger av pushtjänsten som standard.</li>
              <li>
                Öppna <code>brave://settings/privacy</code>.
              </li>
              <li>Slå på Use Google services for push messaging.</li>
              <li>Starta om Brave och försök igen.</li>
            </ol>
          </div>
        ) : null}

        {/*
          Two different offers, and they are never the same offer.

          On Chrome, Edge and Android the browser hands us its own install moment through
          `beforeinstallprompt`, and there is a button because there is something to press. On iOS no
          page can trigger an installation at all, so the only useful thing the portal can do is name
          the two taps in Safari's own words. A button there would be a control that does nothing.

          Both are absent once the portal is already installed: `beforeinstallprompt` stops firing,
          and `isStandalone` is true, so nobody is told to install what they are already running.
        */}
        {showIosInstall || canInstall ? (
          <div className="k-set-line" data-testid="device-install">
            <span className="type-icon" aria-hidden="true">
              <DeviceMobile size={18} />
            </span>
            <div className="primary-cell k-set-fact">
              <strong>{showIosInstall ? "Lägg portalen på hemskärmen" : "Installera portalen"}</strong>
              <span>
                {showIosInstall
                  ? "Tryck på Dela i Safari och välj Lägg till på hemskärmen. Öppna portalen därifrån. Då startar den i eget fönster och kan ta emot notiser."
                  : "Den öppnas i eget fönster och startar snabbare."}
              </span>
            </div>
            {/*
              The iOS branch never has a button, and it is checked here rather than assumed. Safari
              does not fire `beforeinstallprompt`, so `canInstall` is false on iOS in practice, but a
              panel that rendered a dead Installera button if that ever changed would be worse than
              one that says so.
            */}
            {canInstall && !showIosInstall ? (
              <button type="button" className="button" onClick={() => void promptInstall()}>
                Installera
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
